import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import Navbar from "./Navbar";
import "./Leaderboard.css";
import { useNavigate } from "react-router-dom";
import BannerAds from "./BannerAds";

function Leaderboard() {
  const [users, setUsers] = useState([]);
  const [hoveredRow, setHoveredRow] = useState(null);
  const canvasRef = useRef(null);
  const rootRef = useRef(null);
  const animRef = useRef(null);
  const navigate = useNavigate();

  const BASE_URL =
    window.location.hostname === "localhost"
      ? "http://localhost:5000"
      : "https://success-blog-app-backend.onrender.com";

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/blogs/leaderboard`);
      setUsers(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  const openProfile = (author) => {
    navigate(`/authorprofile/${author}`);
  };

  /* ─── PARTICLE CANVAS ─── */
  const COLORS = ["#6332ff", "#00ffc8", "#f472b6", "#60a5fa", "#a78bfa", "#fbbf24"];

  const makeParticle = useCallback((W, H) => ({
    x: Math.random() * W,
    y: Math.random() * H,
    vx: (Math.random() - 0.5) * 0.4,
    vy: (Math.random() - 0.5) * 0.4,
    r: Math.random() * 1.8 + 0.4,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    alpha: Math.random() * 0.6 + 0.2,
    pulse: Math.random() * Math.PI * 2,
  }), []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const root = rootRef.current;
    if (!canvas || !root) return;
    const ctx = canvas.getContext("2d");
    let W = root.offsetWidth;
    let H = root.offsetHeight;
    let particles = [];

    const resize = () => {
      W = root.offsetWidth;
      H = Math.max(root.offsetHeight, window.innerHeight);
      canvas.width = W;
      canvas.height = H;
    };
    resize();
    particles = Array.from({ length: 90 }, () => makeParticle(W, H));

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.pulse += 0.018;
        if (p.x < 0) p.x = W;
        if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H;
        if (p.y > H) p.y = 0;
        const pa = p.alpha * (0.7 + 0.3 * Math.sin(p.pulse));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = pa;
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = particles[i].color;
            ctx.lineWidth = 0.5;
            ctx.globalAlpha = (1 - dist / 100) * 0.12;
            ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1;
      animRef.current = requestAnimationFrame(draw);
    };
    draw();

    const ro = new ResizeObserver(resize);
    ro.observe(root);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(animRef.current);
      ro.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [makeParticle]);

  const RANK_META = [
    { icon: "🥇", glow: "#fbbf24", label: "GOLD" },
    { icon: "🥈", glow: "#94a3b8", label: "SILVER" },
    { icon: "🥉", glow: "#cd7c3a", label: "BRONZE" },
  ];

  const getInitials = (name) =>
    (name || "?").split("_").map((p) => p[0]).join("").toUpperCase().slice(0, 2);

  return (
    <div className="lb-root" ref={rootRef}>
      <canvas className="lb-canvas" ref={canvasRef} />
      <div className="lb-aurora" />

      {/* Background video (kept from original, hidden behind particles) */}
      <video autoPlay loop muted playsInline className="lb-bg-video">
        <source src="/radhakrishna.mp4" type="video/mp4" />
      </video>

      <Navbar />

      <div className="lb-container">

        {/* Header */}
        <div className="lb-header">
          <BannerAds/>
          <div className="lb-header-label">✦ hall of fame</div>
          <h1>Top 10 Contributors</h1>
          <p>ranked by total published blogs</p>
        </div>

        {/* Top 3 podium cards */}
        {users.length >= 3 && (
          <div className="lb-podium">
            {[1, 0, 2].map((rankIdx) => {
              const user = users[rankIdx];
              const meta = RANK_META[rankIdx];
              return (
                <div
                  key={user._id}
                  className={`lb-podium-card lb-podium-${rankIdx}`}
                  style={{ "--glow-color": meta.glow }}
                  onClick={() => openProfile(user._id)}
                >
                  <div className="lb-podium-icon">{meta.icon}</div>
                  <div className="lb-podium-avatar">{getInitials(user._id)}</div>
                  <div className="lb-podium-name">{user._id}</div>
                  <div className="lb-podium-count">{user.totalPosts} posts</div>
                  <div className="lb-podium-badge">{meta.label}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Table */}
        <div className="lb-table-wrap">
          <div className="lb-scanline" />
          <table className="lb-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Author</th>
                <th>Total Blogs</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="3" className="lb-empty-cell">
                    [ NO DATA FOUND ]
                  </td>
                </tr>
              ) : (
                users.map((user, index) => (
                  <tr
                    key={user._id}
                    className={`lb-row${index < 3 ? " lb-top3" : ""}${hoveredRow === index ? " lb-row-hovered" : ""}`}
                    onClick={() => openProfile(user._id)}
                    onMouseEnter={() => setHoveredRow(index)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <td>
                      <span className="lb-rank">
                        {index < 3 ? RANK_META[index].icon : `#${index + 1}`}
                      </span>
                    </td>
                    <td>
                      <div className="lb-author-cell">
                        <div className="lb-avatar">{getInitials(user._id)}</div>
                        <strong>{user._id}</strong>
                      </div>
                    </td>
                    <td>
                      <span className="lb-posts-badge">{user.totalPosts}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

     

      </div>
    </div>
  );
}

export default Leaderboard;